import axios from 'axios';
import type { Modulo, PerfilUsuario, Usuario } from '@eleg360/types';

export interface ConfiguracoesMandato {
  limiar_campanha?: number;
  teto_mensagens_mes?: number;
  silencio_inicio?: string;
  silencio_fim?: string;
  sla_padrao_dias?: number;
  k_anonimato?: number;
  ttl_sessao_horas?: number;
  resposta_modulo_inativo?: '402' | '404';
  /** Números autorizados a receber avisos internos, em E.164. */
  notif_telefone_chefe?: string;
  notif_telefone_parlamentar?: string;
}

export interface RotaPermissao {
  rota: string;
  /** Ação de auditoria declarada, ou null quando a rota é isenta. */
  acao: string | null;
  /** Perfis que o código permite. Vazio = qualquer autenticado. */
  perfisPadrao: PerfilUsuario[];
  /** Exceções gravadas pelo mandato, por perfil. */
  excecoes: Partial<Record<PerfilUsuario, boolean>>;
}

export interface MatrizPermissoes {
  perfis: PerfilUsuario[];
  rotas: RotaPermissao[];
}

export interface ConsumoRecurso {
  usado: number;
  /** null = plano sem limite para este recurso. */
  limite: number | null;
  percentual: number | null;
}

export interface ConsumoDoPlano {
  plano: string;
  rotulo: string;
  usuarios: ConsumoRecurso;
  contatos: ConsumoRecurso;
  mensagens_mes: ConsumoRecurso;
}

export interface Mandato {
  id: string;
  /** Nome civil, como consta no cadastro. */
  nome: string;
  /** Como o gabinete é conhecido publicamente. Nulo enquanto não preenchido. */
  nome_parlamentar?: string | null;
  cargo: string;
  partido?: string | null;
  /** Anos da legislatura. Nulos nos mandatos anteriores a este campo existir. */
  mandato_inicio?: number | null;
  mandato_fim?: number | null;
  escala_padrao: string;
  cidade: string;
  uf: string;
  plano: string;
  modulos: Modulo[];
  configuracoes: ConfiguracoesMandato;
  created_at: string;
  updated_at: string;
}

export interface NovoUsuario {
  email: string;
  nome: string;
  senha: string;
  perfil: PerfilUsuario;
  localidades_permitidas?: string[];
  modulos_permitidos?: Modulo[];
  mfa_habilitado?: boolean;
}

const API_URL = import.meta.env.VITE_API_URL || '/api';
const auth = (token: string) => ({ headers: { Authorization: `Bearer ${token}` } });

export const adminApi = {
  permissoes: async (token: string) => {
    const res = await axios.get<MatrizPermissoes>(`${API_URL}/core/permissoes`, auth(token));
    return res.data;
  },

  definirPermissao: async (
    dados: { rota: string; perfil: PerfilUsuario; permitido?: boolean },
    token: string,
  ) => {
    const res = await axios.put(`${API_URL}/core/permissoes`, dados, auth(token));
    return res.data;
  },

  consumoDoPlano: async (token: string) => {
    const res = await axios.get<ConsumoDoPlano>(`${API_URL}/core/plano`, auth(token));
    return res.data;
  },

  listUsuarios: async (token: string) => {
    const res = await axios.get<Usuario[]>(`${API_URL}/core/usuarios`, auth(token));
    return res.data;
  },

  createUsuario: async (data: NovoUsuario, token: string) => {
    const res = await axios.post<Usuario>(`${API_URL}/core/usuarios`, data, auth(token));
    return res.data;
  },

  updateUsuario: async (id: string, data: Partial<NovoUsuario> & { status?: string }, token: string) => {
    const res = await axios.put<Usuario>(`${API_URL}/core/usuarios/${id}`, data, auth(token));
    return res.data;
  },

  /** Desativa o acesso; o registro permanece para a trilha de auditoria. */
  desativarUsuario: async (id: string, token: string) => {
    await axios.delete(`${API_URL}/core/usuarios/${id}`, auth(token));
  },

  /**
   * Dispara o e-mail de definição de senha para o usuário. Síncrono: a
   * resposta só chega depois que o servidor tentou enviar de fato.
   */
  solicitarRedefinicaoSenha: async (id: string, token: string) => {
    const res = await axios.post<{ enviado: true }>(
      `${API_URL}/core/usuarios/${id}/redefinir-senha`,
      {},
      auth(token),
    );
    return res.data;
  },

  getMandato: async (token: string) => {
    const res = await axios.get<Mandato>(`${API_URL}/core/mandato`, auth(token));
    return res.data;
  },

  updateMandato: async (data: Partial<Mandato>, token: string) => {
    const res = await axios.put<Mandato>(`${API_URL}/core/mandato`, data, auth(token));
    return res.data;
  },
};
